import React, { useEffect, useState, useCallback } from 'react';
import {
  Send,
  Files,
  RotateCw,
  Link as LinkIcon,
  Ban,
  ChevronDown,
  ChevronUp,
  FileSignature,
  Eye,
  PenLine,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import * as SignService from '../service/sign';
import { signerColor } from './editor/sign-overlays';

const REQUEST_STATUS_STYLE = {
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Out for signature', className: 'bg-blue-50 text-blue-700' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700' },
  declined: { label: 'Declined', className: 'bg-red-50 text-red-700' },
  expired: { label: 'Expired', className: 'bg-amber-50 text-amber-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500' },
};

const SIGNER_STATUS_LABEL = {
  waiting: 'Waiting',
  sent: 'Invited',
  viewed: 'Viewed',
  signed: 'Signed',
  declined: 'Declined',
};

/**
 * Track signatures — every request sent from this item.
 *
 * Read-only apart from cancelling: building and sending live in tab 1, so this
 * tab has no send modal. Drafts are excluded server-side; they belong to the
 * builder's drafts list, where they can still be finished.
 */
export default function DocumentsTab({ monday, getSessionToken, itemId, onStartRequest }) {
  const [requests, setRequests] = useState(null);

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.listSignRequests(token, itemId);
      setRequests(res?.ok ? res.requests : []);
    } catch (err) {
      console.error('Failed to load sign requests', err);
      setRequests([]);
    }
  }, [getSessionToken, itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async (request) => {
    const confirmed = await monday
      .execute('confirm', {
        message: `Cancel "${request.name}"? The signing links stop working immediately. Signers are not notified automatically.`,
        confirmButton: 'Cancel request',
        cancelButton: 'Keep it',
      })
      .then((res) => res?.data?.confirm)
      .catch(() => window.confirm(`Cancel "${request.name}"?`));
    if (!confirmed) return;

    try {
      const token = await getSessionToken();
      const res = await SignService.cancelSignRequest(token, request.id);
      if (res?.ok) {
        monday.execute('notice', { message: 'Request cancelled.', type: 'success' });
        load();
      } else {
        monday.execute('notice', { message: res?.message || 'Could not cancel.', type: 'error' });
      }
    } catch (err) {
      console.error('Cancel failed', err);
      monday.execute('notice', { message: 'Could not cancel the request.', type: 'error' });
    }
  };

  const copyLink = (signer) => {
    navigator.clipboard
      ?.writeText(signer.signingUrl)
      .then(() =>
        monday.execute('notice', {
          message: `${signer.roleLabel}'s signing link copied.`,
          type: 'success',
          timeout: 2500,
        }),
      )
      .catch(() => {});
  };

  if (requests === null) {
    return <div className='py-16 text-center text-sm text-gray-500'>Loading…</div>;
  }

  return (
    <div className='max-w-2xl mx-auto px-4 py-5'>
      <div className='mb-3'>
        <h2 className='text-base font-semibold text-gray-900'>Track signatures</h2>
        <p className='text-xs text-gray-500 mt-0.5'>Requests sent from this item.</p>
      </div>

      {requests.length === 0 ? (
        <div className='border border-dashed border-gray-300 rounded-lg py-14 text-center bg-white px-6'>
          <Files size={28} className='mx-auto text-gray-300 mb-2' />
          <div className='text-sm font-medium text-gray-800'>Nothing sent yet</div>
          <p className='text-sm text-gray-500 mt-1 max-w-sm mx-auto'>
            Build a request in the first tab — signers get an email with a secure link and work
            through the documents one at a time.
          </p>
          <button
            onClick={onStartRequest}
            className='mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
          >
            <Send size={14} />
            New request
          </button>
        </div>
      ) : (
        <ul className='space-y-2'>
          {requests.map((r) => {
            const style = REQUEST_STATUS_STYLE[r.status] || REQUEST_STATUS_STYLE.pending;
            const open = r.status === 'pending' || r.status === 'sent';
            return (
              <li key={r.id} className='bg-white border border-gray-200 rounded-lg px-4 py-3'>
                <div className='flex items-center gap-2'>
                  <FileSignature size={16} className='text-gray-400 flex-shrink-0' />
                  <span className='text-sm font-medium text-gray-900 truncate'>{r.name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${style.className}`}>
                    {style.label}
                  </span>
                  <span className='flex-1' />
                  {open && (
                    <>
                      <span className='text-[11px] text-gray-400'>{expiryText(r.expiresAt)}</span>
                      <button
                        onClick={() => handleCancel(r)}
                        title='Cancel this request'
                        className='inline-flex flex-shrink-0 items-center gap-1 px-2 py-1 rounded-md border border-gray-200 text-xs font-medium text-gray-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50'
                      >
                        <Ban size={13} />
                        Cancel
                      </button>
                    </>
                  )}
                </div>

                {/* Documents in the envelope, with their signed state. */}
                {r.documents?.length > 0 && (
                  <div className='mt-2 flex flex-wrap gap-1.5'>
                    {r.documents.map((d, i) => (
                      <span
                        key={d.id}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${
                          d.signedAvailable ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span className='font-mono opacity-60'>{i + 1}</span>
                        {d.name}
                      </span>
                    ))}
                  </div>
                )}

                <div className='mt-2 space-y-1'>
                  {r.signers.map((s) => (
                    <div key={s.signerIndex} className='flex items-center gap-2 text-xs'>
                      <span
                        className='w-2 h-2 rounded-full flex-shrink-0'
                        style={{ background: signerColor(s.signerIndex).color }}
                      />
                      <span className='text-gray-700 font-medium'>{s.roleLabel}</span>
                      <span className='text-gray-500 truncate'>{s.email}</span>
                      <span className='text-gray-400'>
                        · {SIGNER_STATUS_LABEL[s.status] || s.status}
                      </span>
                      {s.documentsTotal > 1 && (
                        <span className='text-gray-400'>
                          ({s.documentsSigned}/{s.documentsTotal})
                        </span>
                      )}
                      {open && (s.status === 'sent' || s.status === 'viewed') && (
                        <button
                          onClick={() => copyLink(s)}
                          title='Copy signing link'
                          className='p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        >
                          <LinkIcon size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <TrackingTimeline getSessionToken={getSessionToken} request={r} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Timeline groups in display order; each collects its audit event types. */
const TIMELINE_GROUPS = [
  { types: ['envelope.sent'], label: 'Sent', icon: Send, verb: 'Sent to' },
  { types: ['document.viewed'], label: 'Viewed', icon: Eye, verb: 'Viewed by' },
  // `needsActor`: a signature with no signatory is not a signature. Finalisation
  // used to be recorded as signature.applied by 'system', which put a second,
  // actorless "Signed by · doc.pdf" row under every document. Retyped on the
  // server, but rows written before that are still in the trail — and dropping
  // them here is right regardless of when they were written.
  { types: ['signature.applied'], label: 'Signed', icon: PenLine, verb: 'Signed by', needsActor: true },
  { types: ['envelope.completed'], label: 'Completed', icon: CheckCircle2, terminal: 'done' },
  { types: ['envelope.declined'], label: 'Declined', icon: Ban, verb: 'Declined by', terminal: 'bad' },
  { types: ['envelope.cancelled'], label: 'Cancelled', icon: Ban, terminal: 'bad' },
  { types: ['envelope.expired'], label: 'Expired', icon: Clock, terminal: 'bad' },
];

/**
 * Per-request tracking timeline, fed by the append-only audit trail. Collapsed
 * by default; events are fetched once on first expand.
 *
 * Events now carry a document, so "Signed" reads per document — which is the
 * only honest rendering once a request holds several.
 */
function TrackingTimeline({ getSessionToken, request }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || data) return;
    try {
      const t = await getSessionToken();
      const res = await SignService.getSignRequestEvents(t, request.id);
      setData(res?.ok ? res : { events: [] });
    } catch (err) {
      console.error('Failed to load tracking events', err);
      setData({ events: [] });
    }
  };

  const groups = data
    ? TIMELINE_GROUPS.map((g) => ({
        ...g,
        events: data.events.filter(
          (e) => g.types.includes(e.eventType) && (!g.needsActor || e.actorEmail),
        ),
      })).filter((g) => g.events.length > 0)
    : [];

  return (
    <div className='mt-2 pt-2 border-t border-gray-100'>
      <button
        onClick={toggle}
        className='flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700'
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Tracking
      </button>

      {expanded && (
        <div className='mt-3 ml-1'>
          {data === null ? (
            <p className='text-xs text-gray-400 flex items-center gap-1.5'>
              <RotateCw size={11} className='animate-spin' /> Loading history…
            </p>
          ) : groups.length === 0 ? (
            <p className='text-xs text-gray-400'>No events recorded yet.</p>
          ) : (
            groups.map((g, gi) => {
              const Icon = g.icon;
              const last = gi === groups.length - 1;
              const tone =
                g.terminal === 'done'
                  ? { ring: 'border-green-600', fg: 'text-green-700' }
                  : g.terminal === 'bad'
                    ? { ring: 'border-gray-400', fg: 'text-gray-500' }
                    : { ring: 'border-blue-600', fg: 'text-blue-600' };
              return (
                <div key={g.label} className='flex gap-3'>
                  <div className='flex flex-col items-center'>
                    <span
                      className={`w-7 h-7 rounded-full border-2 ${tone.ring} ${tone.fg} flex items-center justify-center flex-shrink-0 bg-white`}
                    >
                      <Icon size={13} />
                    </span>
                    {!last && <span className='w-px flex-1 bg-gray-200 my-0.5' />}
                  </div>

                  <div className={`min-w-0 ${last ? '' : 'pb-4'}`}>
                    <p className='text-xs font-semibold text-gray-900 mt-1.5'>{g.label}</p>
                    {g.events.map((e, i) => (
                      <div key={i} className='mt-1'>
                        <p className='text-xs text-gray-700'>
                          {g.verb ? `${g.verb} ` : ''}
                          {e.actorEmail ||
                            (g.label === 'Sent' ? request.signers.map((s) => s.email).join(', ') : '')}
                          {/* Which document this act covered — the point of
                              per-document events. */}
                          {e.documentName && (
                            <span className='text-gray-500'> · {e.documentName}</span>
                          )}
                        </p>
                        <p className='text-[11px] text-gray-500'>{new Date(e.at).toLocaleString()}</p>
                        {e.ip && <p className='text-[11px] text-gray-400'>IP: {e.ip}</p>}
                        {/* Why they declined, read as one more line of the record
                            rather than as an alert: by the time the sender opens
                            the timeline the decline itself is already the news. */}
                        {e.reason && (
                          <p className='text-[11px] text-gray-500 whitespace-pre-wrap break-words'>
                            Decline Reason: {e.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function expiryText(expiresAt) {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return '';
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'expires today';
  return `expires in ${days} ${days === 1 ? 'day' : 'days'}`;
}
